import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const form1095cmailingaddress = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 1095-C Mailing Address | where to mail ACA Forms"
          description="Mail your completed Form 1095-C to the correct IRS address based on your business location and state requirements and stay ACA-compliant with TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-1095-c-mailing-address/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 4,
            mb: 4,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> Form 1095-C </span>{' '}
          </span>{' '}
          Mailing Address: <br />
          Where to mail ACA Forms
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1095-c-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-09-11T09:00:00-05:00"
                      >
                        September 11, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 17, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1095-C Mailing Address at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <Link
                          href="/resources/information-returns/form-1095-c-instructions/"
                          sx={{ color: '#2020ff' }}
                        >
                          Form 1095-C
                        </Link>{' '}
                        {
                          ' reports employer-provided health insurance offer and coverage details under the ACA.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        {'Applicable Large Employers (ALEs) must'}{' '}
                        <Link
                          href="/aca-forms/e-file-form-1095c/"
                          sx={{ color: '#2020ff' }}
                        >
                          file Form 1095-C
                        </Link>{' '}
                        {' for full-time employees.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {
                          ' Paper forms must be sent to the correct IRS address based on the employer’s business location.  '
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        Furnish employee copies and paper-file by February 28;
                        e-file with the IRS by March 31.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Best option:
                        </strong>{' '}
                        {
                          ' E-filing helps reduce errors, speed up processing, track status, and avoid mailing delays. '
                        }
                      </span>
                    </li>
                  </Box>
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  <Link
                    href="/resources/information-returns/form-1095-c-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    Form 1095-C
                  </Link>
                  , Employer-Provided Health Insurance Offer and Coverage is
                  filed by Applicable Large Employers (ALEs) to report details
                  about the health coverage offered to full-time employees under
                  the Affordable Care Act (ACA). This form helps the IRS
                  determine whether employers are meeting ACA requirements and
                  whether employees qualify for premium tax credits.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#2020ff' }}
                  >
                    Filing Form 1095-C
                  </Link>{' '}
                  accurately and on time is essential to avoid IRS penalties and
                  stay compliant. In this guide, you know about{' '}
                  <Link
                    href="/resources/information-returns/form-1095-c-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    1095 c form
                  </Link>{' '}
                  mailing addresses, filing deadlines, available filing methods,
                  and why e-filing is often the faster and more secure option.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                  }}
                  className="gradient-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '20px !important',
                      }}
                    >
                      E-Filing for Faster and More Secure Processing
                    </Typography>
                    <Typography variant="body2">
                      Want faster processing and fewer errors? Switch to
                      e-filing with TaxZerone and skip the mailing delays. File
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        1095 C Form{' '}
                      </strong>{' '}
                      electronically today — starting at just{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        $2.49 per form as low as 0.59 per form for bulk filings!
                      </strong>
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                        backgroundColor: '#0bf473',
                        color: '#333',
                        fontSize: 18,
                        fontWeight: 600,
                        '&:hover, &:focus, &:active': {
                          backgroundColor: '#fff',
                        },
                      }}
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1095cmailingaddress`}
                    >
                      E-file Now
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      backgroundColor: '#fff3b1',
                      py: 1,
                      px: 2,
                      border: '2px dashed #000',
                      color: '#000',
                      lineHeight: 1.3,
                    }}
                  >
                    Grab <strong style={{ fontWeight: 700 }}> 10% off </strong>
                    &nbsp;your filing fee — just enter{' '}
                    <strong
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: '#c02121',
                      }}
                    >
                      TZOIR10
                    </strong>{' '}
                    at checkout.
                  </Box>
                </Box>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where to mail IRS Form 1095-C?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What are the filing deadlines for Form 1095-C?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why e-filing is better than paper filing for Form 1095
                          c?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#efile" sx={{ color: '#0202ff' }}>
                          E-file Form 1095 c with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="where"
                >
                  Where to mail IRS Form 1095-C?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Mail your completed Form{' '}
                  <Link
                    href="/resources/information-returns/form-1095-c-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1095-C
                  </Link>{' '}
                  to the appropriate IRS address listed below. The correct
                  mailing address depends on the state where your business is
                  located.{' '}
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Business Location
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Mailing Address
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Alabama, Arizona, Arkansas, Connecticut, Delaware,
                            Florida, Georgia, Kentucky, Louisiana, Maine,
                            Massachusetts, Mississippi, New Hampshire, New
                            Jersey, New Mexico, New York, North Carolina, Ohio,
                            Pennsylvania, Rhode Island, Texas, Vermont,
                            Virginia, West Virginia
                          </TableCell>

                          <TableCell>
                            Department of the Treasury <br />
                            Internal Revenue Service Center
                            <br /> Austin, TX 73301
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Alaska, California, Colorado, District of Columbia,
                            Hawaii, Idaho, Illinois, Indiana, Iowa, Kansas,
                            Maryland, Michigan, Minnesota, Missouri, Montana,
                            Nebraska, Nevada, North Dakota, Oklahoma, Oregon,
                            South Carolina, South Dakota, Tennessee, Utah,
                            Washington, Wisconsin, Wyoming
                          </TableCell>

                          <TableCell>
                            Department of the Treasury <br /> Internal Revenue
                            Service Center
                            <br /> P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Outside the United States
                          </TableCell>

                          <TableCell>
                            {' '}
                            Department of the Treasury, <br /> Internal Revenue
                            Service Center, <br /> Austin, TX 73301{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b>{' '}
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#2020ff' }}
                  >
                    E-filing Form 1095-C
                  </Link>{' '}
                  simple and easy. TaxZerone also supports California state
                  filing for the{' '}
                  <Link
                    href="/resources/information-returns/form-1095-c-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    1095-c
                  </Link>{' '}
                  form.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Important Paper filing Instructions{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1 } }}>
                  <li>
                    Mail forms in a
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      flat envelope (do not fold){' '}
                    </strong>{' '}
                  </li>
                  <li>Do not staple or paperclip forms</li>
                  <li>
                    Print forms in
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      landscape format{' '}
                    </strong>{' '}
                  </li>
                  <li>
                    Use
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      First-Class Mail{' '}
                    </strong>{' '}
                  </li>
                  <li>
                    If mailing multiple packages, number them consecutively and
                    include Form 1094-C in package number one.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What are the filing deadlines for Form 1095-C?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Employers must meet specific IRS{' '}
                  <Link
                    href="/resources/information-returns/aca-form-due-date/"
                    sx={{ color: '#2020ff' }}
                  >
                    deadlines for Form 1095-C
                  </Link>
                  . Missing these dates can result in{' '}
                  <Link
                    href="/resources/information-returns/aca-forms-penalties/"
                    sx={{ color: '#2020ff' }}
                  >
                    ACA Forms penalties
                  </Link>{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 1.5, '& li': { mb: 1 } }}>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Employee Copies:{' '}
                    </strong>{' '}
                    Employers must furnish Form 1095 C to employees by{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      March 2, 2026,
                    </strong>{' '}
                    for the 2025 Tax year
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Paper Filing with the IRS:{' '}
                    </strong>{' '}
                    If you are filing by mail, the deadline is{' '}
                    <strong style={{ fontWeight: 600 }}> March 2, 2026</strong>
                    .{' '}
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      E-Filing with the IRS:{' '}
                    </strong>{' '}
                    If you file electronically, the deadline is{' '}
                    <strong style={{ fontWeight: 600 }}> March 31, 2026</strong>
                    .{' '}
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesOutlinedIcon />}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Tips :</b>{' '}
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#2020ff' }}
                  >
                    File your Form 1095-C
                  </Link>{' '}
                  before the deadline to avoid IRS penalties
                </Alert>
                <Box sx={{ mt: 3, mb: 3 }}>
                  <Grid
                    container
                    spacing={3}
                    sx={{
                      alignItems: 'stretch',
                    }}
                  >
                    <Grid
                      size={{ xs: 12, sm: 6, md: 6 }}
                      sx={{ display: 'flex' }}
                    >
                      <Box
                        sx={{
                          py: 2,
                          px: 2,
                          backroundcolor: '#f3f3f3',
                          border: '1px solid #e2e6ed',
                          borderRadius: 2,
                          height: '100%',
                          width: '100%',
                          boxSizing: 'border-box',
                          display: 'flex',
                          flexDirection: 'column',
                          textAlign: 'left',
                          background: `linear-gradient( -35deg, rgba(255, 255, 255, 1) 10%
                            ,rgb(255, 255, 255) 45%,
                             rgb(253, 238, 215) 100%)`,
                          '&:hover, &:focus, &:active': {
                            boxShadow: '0 4px 18px rgba(0, 0, 0, 0.15)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <Typography
                          variant="h3"
                          sx={{
                            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                            mb: 2,
                            fontWeight: 600,
                          }}
                        >
                          Need More Time to File with the IRS?
                        </Typography>{' '}
                        <Typography variant="body1">
                          If you can’t meet the IRS filing deadline, request
                          additional time by filing Form 8809{' '}
                        </Typography>
                        <Box
                          sx={{
                            width: '100%',
                            textAlign: 'left',
                            pt: 2,
                            mt: 'auto',
                          }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            sx={{
                              textTransform: 'none',
                              boxShadow: 'none',
                              borderRadius: 2,
                              backgroundColor: '#213e99',
                              color: '#fff',
                              fontSize: 18,
                              fontWeight: 600,
                              '&:hover, &:focus, &:active': {
                                backgroundColor: '#172b6b',
                              },
                            }}
                            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1095cmailingaddress`}
                          >
                            E-file Now
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid
                      size={{ xs: 12, sm: 6, md: 6 }}
                      sx={{ display: 'flex' }}
                    >
                      <Box
                        sx={{
                          py: 2,
                          px: 2,
                          backroundcolor: '#f3f3f3',
                          border: '1px solid #e2e6ed',
                          borderRadius: 2,
                          height: '100%',
                          width: '100%',
                          boxSizing: 'border-box',
                          display: 'flex',
                          flexDirection: 'column',
                          textAlign: 'left',
                          background: `linear-gradient( -35deg, rgba(255, 255, 255, 1) 10%
                            ,rgb(255, 255, 255) 45%,
                             rgb(215, 240, 253) 100%)`,
                          '&:hover, &:focus, &:active': {
                            boxShadow: '0 4px 18px rgba(0, 0, 0, 0.15)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <Typography
                          variant="h3"
                          sx={{
                            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                            mb: 2,
                            fontWeight: 600,
                          }}
                        >
                          Need More Time to Furnish Employee Copies?
                        </Typography>{' '}
                        <Typography variant="body1">
                          If you need 30-day extension for furnishing statements
                          to recipients, you can request an extension using Form
                          15397{' '}
                        </Typography>
                        <Box
                          sx={{
                            width: '100%',
                            textAlign: 'left',
                            pt: 2,
                            mt: 'auto',
                          }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            sx={{
                              textTransform: 'none',
                              boxShadow: 'none',
                              borderRadius: 2,
                              backgroundColor: '#213e99',
                              color: '#fff',
                              fontSize: 18,
                              fontWeight: 600,
                              '&:hover, &:focus, &:active': {
                                backgroundColor: '#172b6b',
                              },
                            }}
                            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1095cmailingaddress`}
                          >
                            E-file Now
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1.5,
                    scrollMarginTop: 83,
                  }}
                  id="why"
                >
                  Why e-filing better than paper filing for Form 1095-c?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#2020ff' }}
                  >
                    E-Filing Form 1095-C
                  </Link>{' '}
                  reduces errors, speeds up IRS processing, and ensures ACA
                  compliance. Electronic submission is faster and more efficient
                  than mailing paper forms.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  The table below highlights why e-filing is the preferred
                  method for&nbsp;
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#2020ff' }}
                  >
                    1095c
                  </Link>{' '}
                  submission.
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Feature
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            E-Filing
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Paper Filing
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Filing Speed{' '}
                            </strong>
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            Instant online submission with quick IRS
                            acknowledgment
                          </TableCell>

                          <TableCell>
                            {' '}
                            Slow processing; may take several weeks
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Error Prevention{' '}
                            </strong>
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            Error Prevention Built-in ACA code validation for
                            Lines 14, 15 & 16 to reduce error before submission
                          </TableCell>
                          <TableCell>
                            {' '}
                            Errors identified only after IRS review
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Filing Status{' '}
                            </strong>
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            Real-time status tracking within your account
                          </TableCell>
                          <TableCell>
                            {' '}
                            No visibility until the IRS processes the forms
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Corrections{' '}
                            </strong>
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            Quick and easy electronic corrections
                          </TableCell>
                          <TableCell>
                            {' '}
                            Requires reprinting and re-mailing forms
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Delivery Risk{' '}
                            </strong>
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            No risk of mail delays or lost forms
                          </TableCell>
                          <TableCell>
                            {' '}
                            Risk of delayed, misplaced or lost mail
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Recordkeeping{' '}
                            </strong>
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            Secure cloud-based digital storage for easy access
                          </TableCell>
                          <TableCell>
                            {' '}
                            Requires physical storage and manual record handling
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Cost-effective{' '}
                            </strong>
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            No printing or mailing expenses
                          </TableCell>
                          <TableCell>
                            {' '}
                            Higher costs for paper, printing, mailing and labor
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Compliance{' '}
                            </strong>
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            IRS-authorized e-file provider ensuring compliant
                            submission
                          </TableCell>
                          <TableCell>
                            {' '}
                            Higher risk of penalties due to mailing or
                            processing delays
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Convenience{' '}
                            </strong>
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            File anytime, anywhere with TaxZerone
                          </TableCell>
                          <TableCell>
                            {' '}
                            Manual and time-consuming filing process
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1.5,
                    scrollMarginTop: 83,
                  }}
                  id="efile"
                >
                  E-file your Form 1095-c with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#2020ff' }}
                  >
                    E-file Form 1095-C
                  </Link>{' '}
                  through TaxZerone—fast, secure, and affordable for Applicable
                  Large Employers. Pricing starts at just{' '}
                  <strong style={{ fontWeight: 600 }}> $2.49 per form</strong>,{' '}
                  and pricing goes as low as{' '}
                  <strong style={{ fontWeight: 600 }}> $0.59 per form</strong>{' '}
                  for bulk filings. TaxZerone ensures accurate ACA reporting and
                  IRS compliance every time.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  TaxZerone supports IRS e-filing and{' '}
                  <strong style={{ fontWeight: 600 }}>
                    California state filing
                  </strong>{' '}
                  helping you meet both federal and California ACA reporting
                  requirements without errors. You can securely deliver
                  recipient copies through{' '}
                  <strong style={{ fontWeight: 600 }}> ZeroneVault </strong> or
                  choose postal mailing—all from a single, easy-to-use platform.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on the IRS
                        guidelines. For more details, please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i109495c.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          IRS
                        </Link>{' '}
                        Instructions of Form 1095-C.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File Form 1095 C quickly and accurately with TaxZerone!
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              E-file now to reduce errors, stay IRS-compliant, and{' '}
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              deliver employee copies securely through ZeroneVault
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1095cmailingaddress`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default form1095cmailingaddress;
